import React, { useState, useRef, useEffect, useMemo } from "react";
import { workshop_programData } from "@/constants";
import { SectionContainerDark } from "../common/sections/SectionContainerDark";
import { program } from "@/interfaces/Workshop";
import ProgramDetailModal from "./ProgramDetailModal";
import { useRouter } from "next/router";
import { HeaderSection } from "../common/headers/HeaderSection";

const ProgramCarousel = () => {
  const router = useRouter();

  /* =======================
   * MAIN TAB
   * ======================= */
  const mainTabs = [
    "Pelatihan Perusahaan / In-House Training",
    "Paket Uji Bersertifikasi BNSP",
  ];

  const [activeMainTab, setActiveMainTab] = useState(mainTabs[0]);

  /* =======================
   * DATA SOURCE
   * ======================= */
  const activeData =
    activeMainTab === "Pelatihan Perusahaan / In-House Training"
      ? workshop_programData["Artificial Inteligence"]
      : workshop_programData["Paket Uji Bersertifikasi BNSP"];

  const subTabs =
    activeMainTab === "Pelatihan Perusahaan / In-House Training"
      ? ["Artificial Inteligence"]
      : [];

  const programsToDisplay: program[] = useMemo(() => {
    if (!activeData) return [];
    return Array.isArray(activeData) ? activeData : [];
  }, [activeData]);

  /* =======================
   * MODAL
   * ======================= */
  const [selectedProgram, setSelectedProgram] = useState<program | null>(null);

  /* =======================
   * RESET MODAL & QUERY SAAT PINDAH TAB
   * ======================= */
  useEffect(() => {
    setSelectedProgram(null);

    router.replace(
      {
        pathname: router.pathname,
        query: {},
      },
      undefined,
      { shallow: true }
    );
  }, [activeMainTab]);

  /* =======================
   * ROUTER QUERY (BNSP ONLY)
   * ======================= */
  const programQuery = router.query["program"];

  useEffect(() => {
    if (
      !programQuery ||
      activeMainTab !== "Paket Uji Bersertifikasi BNSP"
    )
      return;

    const found = programsToDisplay.find(
      (p) => p.title === programQuery
    );

    if (found) {
      setSelectedProgram(found);
    }
  }, [programQuery, programsToDisplay, activeMainTab]);

  /* =======================
   * SCROLL
   * ======================= */
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const updateScrollPosition = () => {
    const container = scrollRef.current;
    if (!container) return;

    setIsAtStart(container.scrollLeft <= 0);
    setIsAtEnd(
      container.scrollLeft + container.clientWidth >=
        container.scrollWidth - 1
    );
  };

  const getCardWidth = () => {
    const card = scrollRef.current?.querySelector(
      ".card"
    ) as HTMLElement | null;
    return card ? card.clientWidth + 16 : 356;
  };

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (!container) return;

    const cardWidth = getCardWidth();
    const scrollAmount =
      window.innerWidth <= 768 ? cardWidth : cardWidth * 3;

    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });

    setTimeout(updateScrollPosition, 300);
  };

  useEffect(() => {
    updateScrollPosition();
    const container = scrollRef.current;
    container?.addEventListener("scroll", updateScrollPosition);
    return () =>
      container?.removeEventListener("scroll", updateScrollPosition);
  }, []);

  /* =======================
   * WHATSAPP
   * ======================= */
  const rerouteToWhatsApp = () => {
    const phoneNumber = "6281234567890";
    const message =
      "Halo, saya ingin training corporate / custom sesuai kebutuhan 😊";

    window.open(
      `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
        message
      )}`,
      "_blank"
    );
  };

  const showArrows = programsToDisplay.length >= 3;

  /* =======================
   * RENDER
   * ======================= */
  return (
    <SectionContainerDark
      id="program-carousel"
      additionalClassName="bg-gray-900 text-white py-[50px] px-4 md:px-12"
    >
      <HeaderSection
        title="Pelatihan Perusahaan & Sertifikat Kompetensi BNSP"
        subtitle="Latih & Kembangkan Kompetensi Karyawan Perusahaan Sesuai Bidang Kompetensi"
        darkMode
      />

      {/* MAIN TABS */}
      <div className="flex md:flex-row flex-col items-center justify-center gap-4 mb-6 md:-mt-4 mt-4">
        <div className="flex gap-4">
          {mainTabs.map((main) => (
            <button
              key={main}
              onClick={() => setActiveMainTab(main)}
              className={`md:px-4 px-1 py-2 rounded font-medium w-auto ${
                activeMainTab === main
                  ? "bg-teal-500 text-white"
                  : "bg-gray-500 hover:bg-gray-400 text-white"
              }`}
            >
              <span className="text-[15px]">{main}</span>
            </button>
          ))}
        </div>

        <button
          onClick={rerouteToWhatsApp}
          className="px-6 py-2 rounded font-medium bg-gray-500 hover:bg-gray-400 text-sm text-white"
        >
          Custom Sesuai Kebutuhan
        </button>
      </div>

      {/* SUB TAB */}
      {subTabs.length > 0 && (
        <div className="flex justify-center gap-3 mb-6 flex-wrap">
          {subTabs.map((sub) => (
            <button
              key={sub}
              className="px-4 py-2 rounded font-medium bg-white text-teal-500 hover:bg-teal-100"
            >
              {sub}
            </button>
          ))}
        </div>
      )}

      {/* CAROUSEL */}
      <div className="relative mx-auto max-w-[1080px]">
        {showArrows && !isAtStart && (
          <button
            onClick={() => scroll("left")}
            className="absolute md:w-12 md:h-12 w-11 h-11 md:-left-5 -left-7 top-1/2 -translate-y-1/2 z-10 shadow p-2 rounded-full flex items-center justify-center -mt-4"
          >
            <img
            src="/img/lms/left-arrow.png"
            alt="Left Hover"
            className="absolute inset-0 m-auto"
          />
          </button>
        )}

        <div className="overflow-hidden">
          <div
            ref={scrollRef}
            className="grid auto-cols-[340px] grid-flow-col overflow-x-auto scrollbar-hide gap-4"
          >
            {programsToDisplay.map((program, index) => (
              <div
                key={index}
                className="card w-[340px] bg-white text-black shadow-md flex flex-col"
              >
                <img
                  src={program.image || "/img/default.jpg"}
                  alt={program.title}
                  className="w-full h-40 object-cover"
                />

                <div className="p-4 flex flex-col justify-between flex-1">
                  <div>
                    <h3 className="font-semibold text-lg">
                      {program.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {program.duration}
                    </p>
                    {program.tags && program.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {program.tags.map((tag, idx) => (
                          <span
                            key={`tag-${idx}`}
                            className="bg-gray-200 text-gray-800 px-2 py-1 rounded-full text-xs font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm text-red-600 font-bold mt-4">
                      {program.price}
                    </p>

                    <div className="flex justify-between mt-2">
                      <button
                        onClick={() => {
                          router.push(
                            {
                              pathname: "/program/bnsp",
                              query: { program: program.title },
                            },
                            undefined,
                            { shallow: true }
                          );
                          setSelectedProgram(program);
                        }}
                        className="text-[#448ada] font-bold hover:underline"
                      >
                        Lihat Detail
                      </button>

                      <a
                        href="https://wa.me/6281215636018"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-teal-600 text-white px-6 py-1 rounded hover:bg-black font-bold"
                      >
                        Daftar
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showArrows && !isAtEnd && (
          <button
            onClick={() => scroll("right")}
            className="absolute md:w-12 md:h-12 w-11 h-11 md:-right-5 -right-2 top-1/2 -translate-y-1/2 z-10 shadow p-2 rounded-full flex items-center justify-center -mt-4"
          >
            <img
            src="/img/lms/right-arrow.png"
            alt="Right Hover"
            className="absolute inset-0 m-auto"
           />
          </button>
        )}
      </div>

      {selectedProgram && (
        <ProgramDetailModal
          program={selectedProgram}
          onClose={() => setSelectedProgram(null)}
        />
      )}
    </SectionContainerDark>
  );
};

export default ProgramCarousel;
